import { useState, useRef, useMemo } from "react";
import { Check, User, UserPlus, UserMinus, Loader2, ChevronDown } from "lucide-react";
import { cn } from "../../lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "../ui/command";
import { useAssignableUsers } from "../../hooks/useUsers";
import { useUpdateTicket } from "../../hooks/useTickets";
import { useAuth } from "../../hooks/useAuth";
import { showToast } from "../../lib/toast";
import { getErrorMessage } from "../../lib/apiErrors";
import type { UserSummary, TicketResponse } from "../../types/tickets";

interface AssigneeSelectorProps {
  ticketId: string;
  ticketOrganizationId: string;
  currentAssignee: UserSummary | null;
  ticket: TicketResponse;
  disabled?: boolean;
}

function getInitials(fullName: string): string {
  const parts = fullName.split(" ").filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

// Generate consistent color based on user ID
function getAvatarColor(userId: string): string {
  const colors = [
    "bg-blue-500",
    "bg-green-500",
    "bg-yellow-500",
    "bg-purple-500",
    "bg-pink-500",
    "bg-indigo-500",
    "bg-red-500",
    "bg-orange-500",
  ];
  const hash = userId.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
}

export function AssigneeSelector({
  ticketId,
  ticketOrganizationId,
  currentAssignee,
  ticket,
  disabled = false,
}: AssigneeSelectorProps) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  // Mobile scroll detection to prevent accidental opens
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);
  const pointerMovedRef = useRef(false);
  const MOVEMENT_THRESHOLD = 10;

  const { user } = useAuth();
  const { data: users = [], isLoading: usersLoading, error: usersError } = useAssignableUsers(ticketOrganizationId);
  const updateTicketMutation = useUpdateTicket();

  // Filter users based on search
  const filteredUsers = useMemo(() => {
    if (!searchValue.trim()) return users;
    const search = searchValue.toLowerCase();
    return users.filter(
      (u) =>
        u.fullName.toLowerCase().includes(search) ||
        u.email.toLowerCase().includes(search)
    );
  }, [users, searchValue]);

  // Check if current user can be assigned (is in assignable users and not already assigned)
  const canAssignToMe = useMemo(() => {
    return (
      user &&
      users.some((u) => u.id === user.id) &&
      currentAssignee?.id !== user.id
    );
  }, [user, users, currentAssignee]);

  const handleSelect = async (userId: string | null) => {
    if (disabled || updateTicketMutation.isPending) return;

    try {
      await updateTicketMutation.mutateAsync({
        id: ticketId,
        updates: {
          subject: ticket.subject,
          description: ticket.description,
          status: ticket.status,
          priority: ticket.priority,
          categoryId: ticket.category?.id || null,
          assignedToId: userId,
          dueAt: ticket.dueAt || null,
        },
      });

      setOpen(false);
      setSearchValue("");

      if (userId) {
        const selectedUser = users.find((u) => u.id === userId);
        showToast.success("Assignee Updated", `Assigned to ${selectedUser?.fullName || "user"}`);
      } else {
        showToast.success("Assignee Removed", "Ticket is now unassigned");
      }
    } catch (error) {
      showToast.error("Error", getErrorMessage(error, "Failed to update assignee"));
    }
  };

  const isPending = updateTicketMutation.isPending;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled || isPending}>
        <button
          type="button"
          className={cn(
            "inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium cursor-pointer",
            "hover:ring-2 hover:ring-ring hover:ring-offset-1 transition-all min-h-[28px]",
            "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1",
            currentAssignee
              ? "bg-primary/10 text-primary dark:bg-primary/20"
              : "bg-muted text-muted-foreground",
            isPending && "opacity-50 cursor-wait",
            disabled && "opacity-50 cursor-not-allowed"
          )}
          onPointerDownCapture={(e) => {
            pointerStartRef.current = { x: e.clientX, y: e.clientY };
            pointerMovedRef.current = false;
            e.stopPropagation();
          }}
          onPointerMove={(e) => {
            if (pointerStartRef.current) {
              const dx = Math.abs(e.clientX - pointerStartRef.current.x);
              const dy = Math.abs(e.clientY - pointerStartRef.current.y);
              if (dx > MOVEMENT_THRESHOLD || dy > MOVEMENT_THRESHOLD) {
                pointerMovedRef.current = true;
              }
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (!pointerMovedRef.current && !disabled && !isPending) {
              setOpen((prev) => !prev);
            }
            pointerMovedRef.current = false;
          }}
        >
          {isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : currentAssignee ? (
            <div
              className={cn(
                "flex h-5 w-5 items-center justify-center rounded-full text-white text-[10px] font-semibold flex-shrink-0",
                getAvatarColor(currentAssignee.id)
              )}
            >
              {getInitials(currentAssignee.fullName)}
            </div>
          ) : (
            <User className="h-3.5 w-3.5 flex-shrink-0" />
          )}
          <span className="max-w-20 truncate">
            {currentAssignee?.fullName || "Unassigned"}
          </span>
          <ChevronDown className="h-3 w-3 opacity-50 flex-shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[250px] p-0"
        align="start"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search users..."
            value={searchValue}
            onValueChange={setSearchValue}
          />
          <CommandList>
            {/* Quick Actions */}
            {(canAssignToMe || currentAssignee) && (
              <>
                <CommandGroup>
                  {canAssignToMe && (
                    <CommandItem
                      onSelect={() => handleSelect(user!.id)}
                      className="cursor-pointer"
                    >
                      <UserPlus className="mr-2 h-4 w-4 text-primary" />
                      <span className="font-medium">Assign to me</span>
                    </CommandItem>
                  )}
                  {currentAssignee && (
                    <CommandItem
                      onSelect={() => handleSelect(null)}
                      className="cursor-pointer"
                    >
                      <UserMinus className="mr-2 h-4 w-4" />
                      <span>Unassign</span>
                    </CommandItem>
                  )}
                </CommandGroup>
                <CommandSeparator />
              </>
            )}

            {/* User List */}
            {usersLoading ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                <Loader2 className="mx-auto h-4 w-4 animate-spin mb-2" />
                Loading users...
              </div>
            ) : usersError ? (
              <div className="py-6 text-center text-sm text-destructive">
                Failed to load users
              </div>
            ) : filteredUsers.length === 0 ? (
              <CommandEmpty>
                {searchValue ? `No users found for "${searchValue}"` : "No assignable users"}
              </CommandEmpty>
            ) : (
              <CommandGroup heading="Team Members">
                {filteredUsers.map((assignee) => (
                  <CommandItem
                    key={assignee.id}
                    value={assignee.id}
                    onSelect={() => handleSelect(assignee.id)}
                    className="cursor-pointer"
                  >
                    <div className="flex items-center gap-2 w-full">
                      <div
                        className={cn(
                          "flex h-6 w-6 items-center justify-center rounded-full text-white text-xs font-medium flex-shrink-0",
                          getAvatarColor(assignee.id)
                        )}
                      >
                        {getInitials(assignee.fullName)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate">{assignee.fullName}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {assignee.email}
                        </p>
                      </div>
                      {currentAssignee?.id === assignee.id && (
                        <Check className="h-4 w-4 text-primary flex-shrink-0" />
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
